import { NodeViewContent, type NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import { cn } from "@workith/ui";
import { Check, Copy } from "lucide-react";
import { type ChangeEvent, useState } from "react";

const LANGUAGES = [
  { value: "plaintext", label: "Plain Text" },
  { value: "js", label: "JavaScript" },
  { value: "ts", label: "TypeScript" },
  { value: "html", label: "HTML" },
  { value: "css", label: "CSS" },
  { value: "python", label: "Python" },
  { value: "mermaid", label: "Mermaid Diagram" },
];

export const CodeBlockNodeView = (props: NodeViewProps) => {
  const { editor, node, updateAttributes } = props;
  const [copied, setCopied] = useState(false);
  const isEditable = editor.isEditable;

  const handleCopy = () => {
    navigator.clipboard
      .writeText(node.textContent)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((error) => {
        console.error("Failed to copy code block:", error);
        alert("Failed to copy code");
      });
  };

  const handleLanguageChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (!isEditable) {
      return;
    }

    updateAttributes({ language: event.target.value });
  };

  return (
    <NodeViewWrapper className="code-block-wrapper my-4 relative rounded-md border border-border bg-muted/50 overflow-hidden shadow-sm">
      <div
        className="code-block-header flex items-center justify-between px-4 py-2 bg-muted border-b border-border"
        contentEditable={false}
      >
        <select
          className={cn(
            "text-xs font-mono bg-transparent text-muted-foreground outline-none appearance-none",
            isEditable ? "hover:text-foreground cursor-pointer" : "cursor-default opacity-70"
          )}
          value={node.attrs.language || "plaintext"}
          onChange={handleLanguageChange}
          onMouseDown={(e) => e.preventDefault()}
          disabled={!isEditable}
        >
          <option value="null" disabled className="bg-popover text-muted-foreground">
            Language
          </option>
          {LANGUAGES.map((lang) => (
            <option
              key={lang.value}
              value={lang.value}
              className="bg-popover text-popover-foreground"
            >
              {lang.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          className={cn(
            "flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors",
            copied
              ? "text-green-600 bg-green-100"
              : "text-muted-foreground hover:text-foreground hover:bg-accent"
          )}
          onClick={handleCopy}
          onMouseDown={(e) => e.preventDefault()}
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>

      <pre className="!m-0 !bg-transparent !p-4 !rounded-none">
        <code>
          <NodeViewContent />
        </code>
      </pre>
    </NodeViewWrapper>
  );
};
